/**
 * Account profile API (DOP-266).
 *
 * Talks to `apiFetch` directly rather than through `apiService` because both
 * calls here need something that layer throws away: the profile change returns
 * a body the page re-renders from, and a rejected password change carries the
 * REASON it was rejected in `detail` — "Current password is incorrect" is the
 * entire content of that error, and `ApiError` reduces it to "400 Bad Request".
 */

import { z } from "zod/v3";
import { apiFetch } from "../utils/apiClient";
import { apiErrorCode, apiErrorMessage } from "../utils/apiErrorDetail";

const AUTH_BASE = "/api/v3/auth";

const userProfileSchema = z.object({
	id: z.string(),
	email: z.string(),
	full_name: z.string().nullable().optional(),
	client_name: z.string().nullable().optional(),
	has_password: z.boolean(),
});

export type UserProfile = z.infer<typeof userProfileSchema>;

/** An API refusal that still knows why. */
export class ProfileError extends Error {
	constructor(
		message: string,
		public readonly code: string | null = null,
	) {
		super(message);
		this.name = "ProfileError";
	}
}

/** Turn a non-ok response into a ProfileError carrying the server's sentence. */
const toProfileError = async (
	response: Response,
	fallback: string,
): Promise<ProfileError> => {
	try {
		const body = await response.json();
		return new ProfileError(
			apiErrorMessage(body?.detail, fallback),
			apiErrorCode(body?.detail),
		);
	} catch {
		return new ProfileError(fallback);
	}
};

export const getMyProfile = async (): Promise<UserProfile> => {
	const response = await apiFetch(`${AUTH_BASE}/users/me/profile`, {
		method: "GET",
	});
	if (!response.ok) {
		throw await toProfileError(response, "Could not load your account details.");
	}
	return userProfileSchema.parse(await response.json());
};

/**
 * Rename the account. Goes through fastapi-users' own `PATCH /users/me`, which
 * already owns this field — a second endpoint would just be a second place for
 * the name to be written.
 */
export const updateFullName = async (fullName: string): Promise<void> => {
	const response = await apiFetch(`${AUTH_BASE}/users/me`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ full_name: fullName }),
	});
	if (!response.ok) {
		throw await toProfileError(response, "Could not save your name.");
	}
};

const changePasswordResponseSchema = z.object({
	access_token: z.string(),
	token_type: z.string(),
});

export type ChangePasswordResult = z.infer<typeof changePasswordResponseSchema>;

/**
 * Succeeding here REVOKES the token the caller is holding — the server stamps
 * `password_changed_at` and refuses every token issued before it — so the
 * response carries a replacement. Callers must install it.
 */
export const changePassword = async (
	currentPassword: string,
	newPassword: string,
): Promise<ChangePasswordResult> => {
	const response = await apiFetch(`${AUTH_BASE}/users/me/change-password`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			current_password: currentPassword,
			new_password: newPassword,
		}),
	});
	if (!response.ok) {
		throw await toProfileError(response, "Could not change your password.");
	}
	return changePasswordResponseSchema.parse(await response.json());
};
